import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Heading,
  Text,
  Progress,
  IconButton,
  Flex,
  HStack,
  VStack,
  Button,
  Collapse,
  Divider,
  Spinner,
  Textarea,
  FormControl,
  FormErrorMessage,
  useToast,
  Tag,
  TagLabel,
  TagLeftIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
} from '@chakra-ui/icons';
import {
  Sparkles,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  Users,
  Calendar,
  FileText,
} from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import { Rock, User, Milestone, ActionItem, Issue, RockNote } from '../../types';
import { Card } from '../../../../components/Card';
import { calculateMilestoneProgress, getAiScoreColor, getComputedRockStatus, getCollaboratorType } from '../rocks.util';
import { ResolveIssueModal } from './ResolveIssueModal';
import { MilestonesSection } from './MilestonesSection';
import { ActionItemsSection } from './ActionItemsSection';
import { IssuesSection } from './IssuesSection';

interface RockCardDetailedProps {
  rock: Rock;
  users: User[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onAchieve: () => void;
  onDrop: () => void;
  onEvaluate: () => void;
  onStatusChange: (status: 'on-track' | 'off-track') => void;
  onIssueClick?: (rockId: string, hasOpenIssues?: boolean) => void;
  onResolveIssue?: (issueId: string, resolutionNotes: string, relatedRockId: string) => Promise<void>;
  refreshTrigger?: number;
  refreshMilestonesTrigger?: number;
  refreshActionItemsTrigger?: number;
  aiScore?: number | null;
  loadMilestonesForRock?: (rockId: string) => Promise<Milestone[]>;
  loadActionItemsForRock?: (rockId: string) => Promise<ActionItem[]>;
  loadIssuesForRock?: (rockId: string) => Promise<Issue[]>;
  subscribeToIssues?: (rockIds: string[], callback: (issues: Issue[]) => void) => (() => void);
  saveRockUpdate?: (rockId: string, note: string, currentUserId: string) => Promise<void>;
  loadPreviousNotes?: (rockId: string) => Promise<RockNote[]>;
  currentUserId?: string;
  addMilestone?: (rockId: string, task: string, dueDate: string, sortOrder: number) => Promise<void>;
  updateMilestone?: (milestoneId: string, task: string, dueDate: string) => Promise<void>;
  toggleMilestone?: (milestoneId: string, complete: boolean) => Promise<void>;
  addActionItem?: (rockId: string, task: string, assigneeId: string | null, dueDate: string) => Promise<void>;
  updateActionItem?: (actionItemId: string, task: string, assigneeId: string | null, dueDate: string) => Promise<void>;
  toggleActionItem?: (actionItemId: string, complete: boolean) => Promise<void>;
  updateIssue?: (issueId: string, name: string, description: string) => Promise<void>;
  subscribeToMilestonesForRock?: (rockId: string, callback: (milestones: Milestone[]) => void) => (() => void);
  subscribeToActionItemsForRock?: (rockId: string, callback: (actionItems: ActionItem[]) => void) => (() => void);
  isReadOnly?: boolean;
}

/**
 * Rock Card Detailed Component
 * Displays an expandable rock card with milestones, action items, and issues
 */
const RockCardDetailedComponent: React.FC<RockCardDetailedProps> = ({
  rock,
  users,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onAchieve,
  onDrop,
  onEvaluate,
  onStatusChange,
  onIssueClick,
  onResolveIssue,
  refreshTrigger,
  refreshMilestonesTrigger,
  refreshActionItemsTrigger,
  aiScore,
  loadMilestonesForRock,
  loadActionItemsForRock,
  loadIssuesForRock,
  subscribeToIssues,
  saveRockUpdate,
  loadPreviousNotes,
  currentUserId,
  addMilestone,
  updateMilestone,
  toggleMilestone,
  addActionItem,
  updateActionItem,
  toggleActionItem,
  updateIssue,
  subscribeToMilestonesForRock,
  subscribeToActionItemsForRock,
  isReadOnly = false,
}) => {
  const toast = useToast();
  const {
    isOpen: isNotesModalOpen,
    onOpen: onNotesModalOpen,
    onClose: onNotesModalClose,
  } = useDisclosure();
  
  const {
    isOpen: isResolveModalOpen,
    onOpen: onResolveModalOpen,
    onClose: onResolveModalClose,
  } = useDisclosure();

  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [selectedIssueToResolve, setSelectedIssueToResolve] = useState<Issue | null>(null);

  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingMilestones, setLoadingMilestones] = useState(false);
  const [loadingActionItems, setLoadingActionItems] = useState(false);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  
  const [previousNotes, setPreviousNotes] = useState<RockNote[]>([]);
  
  const isLoadingRef = useRef(false);
  
  // Refs for data loading functions to be used in effects without triggering re-runs
  const loadMilestonesRef = useRef(loadMilestonesForRock);
  const loadActionItemsRef = useRef(loadActionItemsForRock);
  const loadIssuesRef = useRef(loadIssuesForRock);

  // Real-time subscriptions for milestones and action items
  useEffect(() => {
    if (!rock.id || !subscribeToMilestonesForRock || !subscribeToActionItemsForRock) return;
    
    // Subscribe to milestones
    const unsubscribeMilestones = subscribeToMilestonesForRock(rock.id, (updatedMilestones) => {
      setMilestones(updatedMilestones);
    });
    
    // Subscribe to action items
    const unsubscribeActionItems = subscribeToActionItemsForRock(rock.id, (updatedActionItems) => {
      // Filter out dropped action items for display in the rock card
      // (Unless we want to show dropped items in a specific view, but standard view hides them)
      const activeActionItems = updatedActionItems.filter(item => !item.dropped);
      setActionItems(activeActionItems);
    });

    return () => {
      unsubscribeMilestones();
      unsubscribeActionItems();
    };
  }, [rock.id, subscribeToMilestonesForRock, subscribeToActionItemsForRock]);

  // Real-time subscription for issues
  useEffect(() => {
    if (!rock.id) return;
    
    const unsubscribeIssues = subscribeToIssues ? subscribeToIssues([rock.id], (updatedIssues) => {
      setIssues(updatedIssues);
    }) : () => {};

    return () => unsubscribeIssues();
  }, [rock.id, subscribeToIssues]);

  // Store functions in refs to prevent re-renders
  const loadPreviousNotesRef = useRef(loadPreviousNotes);
  
  // Update refs when functions change (without triggering main effect)
  loadMilestonesRef.current = loadMilestonesForRock;
  loadActionItemsRef.current = loadActionItemsForRock;
  loadIssuesRef.current = loadIssuesForRock;
  loadPreviousNotesRef.current = loadPreviousNotes;

  // Memoize owner and collaboratorUsers to prevent unnecessary recalculations
  const owner = useMemo(() => users.find((u) => u.id === rock.ownerId), [users, rock.ownerId]);
  const ownerName = useMemo(() => owner?.name || 'Unknown', [owner]);
  
  // Derive collaboratorUsers from rock.collaborators array
  const collaboratorUsers = useMemo(() => 
    rock.collaborators
      ?.map((id) => users.find((u) => u.id === id))
      .filter((u): u is User => u !== undefined) || [],
    [rock.collaborators, users]
  );

  // Calculate progress from milestones if available, otherwise use rock.progress
  const displayProgress = useMemo(() => {
    if (milestones.length > 0) {
      return calculateMilestoneProgress(milestones);
    }
    return rock.progress || 0;
  }, [milestones, rock.progress]);

  // Track what we've loaded to prevent unnecessary reloads
  const lastLoadedRockIdRef = useRef<string | null>(null);
  const lastRefreshTriggerRef = useRef<number>(refreshTrigger);
  const lastMilestonesTriggerRef = useRef<number>(refreshMilestonesTrigger);
  const lastActionItemsTriggerRef = useRef<number>(refreshActionItemsTrigger);
  const lastExpandedRef = useRef<boolean>(false);
  
  // Initial Load or Full Refresh
  useEffect(() => {
    // Only proceed if expanded
    if (!isExpanded) {
      lastExpandedRef.current = false;
      return;
    }

    // Determine if we need to load everything:
    // 1. First time expanding this rock
    // 2. Rock ID changed
    // 3. Global Refresh trigger changed
    const needsFullLoad =
      (!lastExpandedRef.current && isExpanded) || // Just expanded
      lastLoadedRockIdRef.current !== rock.id || // Different rock
      (lastExpandedRef.current && lastRefreshTriggerRef.current !== refreshTrigger); // Global refresh

    if (!needsFullLoad) {
      return;
    }

    // Skip if functions are not available
    if (!loadMilestonesRef.current || !loadActionItemsRef.current || !loadIssuesRef.current) {
      return;
    }

    const loadData = async () => {
      if (isLoadingRef.current) return;
      
      isLoadingRef.current = true;
      setLoadingDetails(true);
      
      try {
        const [milestonesData, actionItemsData, issuesData] = await Promise.all([
          loadMilestonesRef.current!(rock.id),
          loadActionItemsRef.current!(rock.id),
          loadIssuesRef.current!(rock.id),
        ]);
        setMilestones(milestonesData);
        setActionItems(actionItemsData);
        setIssues(issuesData);
        
        // Load previous notes if function is available
        if (loadPreviousNotesRef.current) {
          setLoadingNotes(true);
          try {
            const notesData = await loadPreviousNotesRef.current(rock.id);
            setPreviousNotes(notesData);
          } catch (error) {
            console.error('Error loading previous notes:', error);
          } finally {
            setLoadingNotes(false);
          }
        }
        
        // Update tracking refs
        lastLoadedRockIdRef.current = rock.id;
        lastRefreshTriggerRef.current = refreshTrigger;
        lastMilestonesTriggerRef.current = refreshMilestonesTrigger;
        lastActionItemsTriggerRef.current = refreshActionItemsTrigger;
        lastExpandedRef.current = isExpanded;
      } catch (error) {
        console.error('Error loading rock details:', error);
      } finally {
        setLoadingDetails(false);
        isLoadingRef.current = false;
      }
    };

    loadData();
  }, [isExpanded, refreshTrigger, rock.id, refreshMilestonesTrigger, refreshActionItemsTrigger]);

  // Specific Refresh for Milestones
  useEffect(() => {
    // Only refresh if expanded AND trigger actually changed
    // IMPORTANT: Check that trigger is different from last time
    if (!isExpanded || lastMilestonesTriggerRef.current === refreshMilestonesTrigger || !loadMilestonesRef.current) {
      return;
    }

    const refreshMilestones = async () => {
      setLoadingMilestones(true);
      try {
        const data = await loadMilestonesRef.current!(rock.id);
        setMilestones(data);
        lastMilestonesTriggerRef.current = refreshMilestonesTrigger;
      } catch (error) {
        console.error('Error refreshing milestones:', error);
      } finally {
        setLoadingMilestones(false);
      }
    };

    refreshMilestones();
  }, [isExpanded, refreshMilestonesTrigger, rock.id]);

  // Specific Refresh for Action Items
  useEffect(() => {
    // Only refresh if expanded AND trigger actually changed
    if (!isExpanded || lastActionItemsTriggerRef.current === refreshActionItemsTrigger || !loadActionItemsRef.current) {
      return;
    }

    const refreshActionItems = async () => {
      setLoadingActionItems(true);
      try {
        const data = await loadActionItemsRef.current!(rock.id);
        setActionItems(data);
        lastActionItemsTriggerRef.current = refreshActionItemsTrigger;
      } catch (error) {
        console.error('Error refreshing action items:', error);
      } finally {
        setLoadingActionItems(false);
      }
    };

    refreshActionItems();
  }, [isExpanded, refreshActionItemsTrigger, rock.id]);

  // Real-time subscription for Issues
  useEffect(() => {
    // Only set up subscription if expanded and subscribeToIssues is available
    if (!isExpanded || !subscribeToIssues) {
      // Clear issues when collapsed
      if (!isExpanded) {
        setIssues([]);
      }
      return;
    }

    // Set loading state only on initial load (when rock ID changes or first expansion)
    const isInitialLoad = lastLoadedRockIdRef.current !== rock.id || !lastExpandedRef.current;
    if (isInitialLoad) {
      setLoadingIssues(true);
    }

    // Set up snapshot listener for this rock's issues
    const unsubscribe = subscribeToIssues([rock.id], (updatedIssues) => {
      // Filter to only issues for this rock (subscribeToIssues filters by rockIds, but double-check)
      const rockIssues = updatedIssues.filter((issue) => issue.relatedRockId === rock.id);
      setIssues(rockIssues);
      setLoadingIssues(false);
    });

    // Cleanup subscription on unmount or when dependencies change
    return () => {
      unsubscribe();
    };
  }, [isExpanded, rock.id, subscribeToIssues]);

  // Load previous notes when modal opens (always refresh to get latest)
  useEffect(() => {
    if (isNotesModalOpen && loadPreviousNotesRef.current && !loadingNotes) {
      setLoadingNotes(true);
      loadPreviousNotesRef.current(rock.id)
        .then((notesData) => {
          setPreviousNotes(notesData);
        })
        .catch((error) => {
          console.error('Error loading previous notes:', error);
        })
        .finally(() => {
          setLoadingNotes(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNotesModalOpen, rock.id]);

  const formatDate = (date: Date | string) => {
    if (date instanceof Date) {
      return date.toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString();
    }
    if (timestamp instanceof Date) {
      return timestamp.toLocaleDateString();
    }
    return new Date(timestamp).toLocaleDateString();
  };

  // Validation schema for Latest Update form
  const updateValidationSchema = Yup.object({
    note: Yup.string()
      .required('Update note is required')
      .min(3, 'Update note must be at least 3 characters'),
  });

  const handleSaveUpdate = async (values: { note: string }, { setSubmitting, resetForm }: any) => {
    if (!saveRockUpdate || !currentUserId) {
      toast({
        title: 'Error',
        description: 'Unable to save update. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setSubmitting(false);
      return;
    }

    try {
      await saveRockUpdate(rock.id, values.note, currentUserId);
      toast({
        title: 'Update saved',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      // Clear form values
      resetForm({ values: { note: '' } });
      
      // Reload previous notes to show the new one
      if (loadPreviousNotesRef.current) {
        setLoadingNotes(true);
        try {
          const notesData = await loadPreviousNotesRef.current(rock.id);
          setPreviousNotes(notesData);
        } catch (error) {
          console.error('Error reloading previous notes:', error);
        } finally {
          setLoadingNotes(false);
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save update',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const milestoneProgress = useMemo(() => calculateMilestoneProgress(milestones), [milestones]);
  
  // Memoize issue arrays - filter open/in-progress vs resolved
  const openIssues = useMemo(() => issues.filter(issue => issue.status === 'open' || issue.status === 'in-progress'), [issues]);
  
  // Compute rock status from open issues count
  // Rock with any open issues = off-track, Rock with no open issues = on-track
  const computedStatus = useMemo(() => getComputedRockStatus(rock, openIssues), [rock, openIssues]);

  // Store toggle functions in refs to prevent handler recreation
  const onResolveIssueRef = useRef(onResolveIssue);
  const loadIssuesForRockRef = useRef(loadIssuesForRock);
  const loadMilestonesForRockRef = useRef(loadMilestonesForRock);
  const loadActionItemsForRockRef = useRef(loadActionItemsForRock);
  
  onResolveIssueRef.current = onResolveIssue;
  loadIssuesForRockRef.current = loadIssuesForRock;
  loadMilestonesForRockRef.current = loadMilestonesForRock;
  loadActionItemsForRockRef.current = loadActionItemsForRock;

  // Handle confirming resolution
  const handleConfirmResolve = async (resolutionNotes: string) => {
    if (!selectedIssueToResolve || !onResolveIssueRef.current) return;
    
    try {
      await onResolveIssueRef.current(selectedIssueToResolve.id, resolutionNotes, rock.id);
      
      // Reload issues
      if (loadIssuesForRockRef.current) {
        const updatedIssues = await loadIssuesForRockRef.current(rock.id);
        setIssues(updatedIssues);
      }
      
      toast({
        title: 'Issue Resolved',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to resolve issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleMarkOnTrack = async (resolutionNotes: string) => {
    if (!selectedIssueToResolve || !onResolveIssueRef.current) return;
    
    try {
      // First resolve the issue
      await onResolveIssueRef.current(selectedIssueToResolve.id, resolutionNotes, rock.id);
      // Then mark as on-track
      onStatusChange('on-track');

      // Reload issues
      if (loadIssuesForRockRef.current) {
        const updatedIssues = await loadIssuesForRockRef.current(rock.id);
        setIssues(updatedIssues);
      }
      
      toast({
        title: 'Issue Resolved & Rock On Track',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to resolve issue',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };


  // Check if the issue being resolved is the last one
  const isLastIssue = useMemo(() => {
    if (!selectedIssueToResolve) return false;
    // openIssues includes the one being resolved
    return openIssues.length === 1 && openIssues[0].id === selectedIssueToResolve.id;
  }, [selectedIssueToResolve, openIssues]);

  // Refresh handlers to pass to child components
  const handleRefreshMilestones = useCallback(async () => {
    if (loadMilestonesForRockRef.current) {
      const updatedMilestones = await loadMilestonesForRockRef.current(rock.id);
      setMilestones(updatedMilestones);
    }
  }, [rock.id]);

  const handleRefreshActionItems = useCallback(async () => {
    if (loadActionItemsRef.current) {
      const updatedActionItems = await loadActionItemsRef.current(rock.id);
      setActionItems(updatedActionItems);
    }
  }, [rock.id]);

  const handleRefreshIssues = useCallback(async () => {
    if (loadIssuesForRockRef.current) {
      const updatedIssues = await loadIssuesForRockRef.current(rock.id);
      setIssues(updatedIssues);
    }
  }, [rock.id]);

  // Color mode values for dark mode support
  const cardBg = useColorModeValue('white', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const textColorLight = useColorModeValue('gray.500', 'gray.400');
  const notesBg = useColorModeValue('gray.50', 'gray.800');

  return (
    <Card
      id={`rock-${rock.id}`}
      borderLeft="4px"
      borderLeftColor={
        computedStatus === 'off-track'
          ? 'red.500'
          : 'green.500'
      }
      bg={cardBg}
    >
      <Box p={4}>
        {/* Rebuilt Header Section */}
        <VStack align="stretch" spacing={3} w="100%" mb={4}>
          <Flex justify="space-between" align="start" w="100%">
            {/* Left Side: Expand/Collapse, Owner, Collaborators, Status */}
            <VStack align="start" spacing={2}>
              <HStack spacing={2} align="center">
                <IconButton
                  aria-label="Expand/Collapse"
                  icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  variant="ghost"
                  size="sm"
                  onClick={onToggleExpand}
                />
                <HStack spacing={2} wrap="wrap">
                  <Tag size="sm" colorScheme="blue" variant="outline">
                    <TagLeftIcon as={Users} w={3} h={3} mr={1} />
                    <TagLabel>Owner: {ownerName}</TagLabel>
                  </Tag>
                {collaboratorUsers.length > 0 && (
                  <>
                    {collaboratorUsers.map(collab => {
                      const type = getCollaboratorType(rock, collab.id);
                      return (
                        <Tag key={collab.id} size="sm" colorScheme="green" variant="outline">
                          <TagLabel>
                            {collab.name}
                            {type && <Text as="span" fontSize="xs" ml={1} verticalAlign="super">{type.replace(',', ', ')}</Text>}
                          </TagLabel>
                        </Tag>
                      );
                    })}
                  </>
                )}
                </HStack>
              </HStack>
              <HStack spacing={2}>
                <Text fontSize="sm" fontWeight="medium">Status:</Text>
                <Button
                  size="xs"
                  colorScheme="green"
                  variant={computedStatus === 'on-track' ? 'solid' : 'outline'}
                  onClick={() => onStatusChange('on-track')}
                  isDisabled={isReadOnly}
                >
                  On Track
                </Button>
                <Button
                  size="xs"
                  colorScheme="red"
                  variant={computedStatus === 'off-track' ? 'solid' : 'outline'}
                  onClick={() => onStatusChange('off-track')}
                  isDisabled={isReadOnly}
                >
                  Off Track
                </Button>
              </HStack>
            </VStack>

            {/* Right Side: Progress, Week, Due, Actions */}
            <VStack align="end" spacing={2} flexShrink={0}>
              <HStack spacing={4}>
                <Box textAlign="right">
                  <Heading size="md" color={headingColor}>{displayProgress}%</Heading>
                  <Text fontSize="xs" color={textColorLight}>Week {rock.weekNumber}/13</Text>
                </Box>
                {aiScore !== null && aiScore !== undefined && (
                  <Tag 
                    size="sm" 
                    colorScheme={
                      aiScore >= 70 ? 'green' : 
                      aiScore >= 50 ? 'yellow' : 
                      'red'
                    }
                    variant="solid"
                    h="100%"
                  >
                    <TagLabel>AI: {aiScore}/100</TagLabel>
                  </Tag>
                )}
                <Tag size="sm" colorScheme="gray" variant="solid" h="100%">
                  <TagLeftIcon as={Calendar} w={3} h={3} mr={1} />
                  <TagLabel>Due: {formatDate(rock.dueDate)}</TagLabel>
                </Tag>
              </HStack>
              {/* Action Icons */}
              <HStack spacing={1}>
                <IconButton
                  aria-label="Get AI Evaluation"
                  icon={<Icon as={Sparkles} w={5} h={5} />}
                  variant="ghost"
                  size="sm"
                  colorScheme="purple"
                  onClick={onEvaluate}
                  title="Get AI Evaluation"
                  isDisabled={isReadOnly}
                />
                <IconButton
                  aria-label="Edit Rock"
                  icon={<Icon as={Pencil} w={5} h={5} />}
                  variant="ghost"
                  size="sm"
                  colorScheme="gray"
                  onClick={onEdit}
                  title="Edit Rock"
                  isDisabled={isReadOnly}
                />
                <IconButton
                  aria-label="Delete Rock"
                  icon={<Icon as={Trash2} w={5} h={5} />}
                  variant="ghost"
                  size="sm"
                  colorScheme="red"
                  onClick={onDelete}
                  title="Delete Rock"
                  isDisabled={isReadOnly}
                />
                <IconButton
                  aria-label="Achieve Rock"
                  icon={<Icon as={CheckCircle2} w={5} h={5} />}
                  variant="ghost"
                  size="sm"
                  colorScheme="green"
                  onClick={onAchieve}
                  title="Achieve Rock"
                  isDisabled={isReadOnly}
                />
                <IconButton
                  aria-label="Drop Rock"
                  icon={<Icon as={XCircle} w={5} h={5} />}
                  variant="ghost"
                  size="sm"
                  colorScheme="orange"
                  onClick={onDrop}
                  title="Drop Rock"
                  isDisabled={isReadOnly}
                />
              </HStack>
            </VStack>
          </Flex>

          {/* Rock Title, Measurable, and Milestones Progress Bar */}
          <VStack align="stretch" spacing={2}>
            <Heading size="md" mb={0}>{rock.rock}</Heading>
            <Text color={textColor} mt={0}>
              <Text as="span" fontWeight="medium">Measurable:</Text> {rock.measurable}
            </Text>
            {aiScore !== null && aiScore !== undefined && isExpanded && (
              <Box textAlign="center" py={2}>
                <Text
                  fontSize="lg"
                  fontWeight="bold"
                  color={getAiScoreColor(aiScore)}
                >
                  {aiScore}%
                </Text>
                <Text fontSize="sm" color="gray.500">
                  AI Confidence Score
                </Text>
              </Box>
            )}
            {milestones.length > 0 && isExpanded && (
              <Box>
                <Flex justify="space-between" align="center" mb={1}>
                  <Text fontSize="xs" color={textColorLight}>
                    Milestones: {milestoneProgress}%
                  </Text>
                </Flex>
                <Progress value={milestoneProgress} colorScheme="blue" size="sm" borderRadius="md" />
              </Box>
            )}
          </VStack>
        </VStack>

        {/* Expanded Content */}
        <Collapse in={isExpanded} animateOpacity>
          {loadingDetails ? (
            <Box textAlign="center" py={4}>
              <Spinner size="md" />
            </Box>
          ) : (
            <VStack spacing={4} align="stretch" mt={4}>
              <Divider />

              <MilestonesSection
                milestones={milestones}
                rockId={rock.id}
                addMilestone={addMilestone}
                updateMilestone={updateMilestone}
                toggleMilestone={toggleMilestone}
                onRefresh={handleRefreshMilestones}
              />
              {loadingMilestones && <Progress size="xs" isIndeterminate colorScheme="blue" />}

              <ActionItemsSection
                actionItems={actionItems}
                rockId={rock.id}
                users={users}
                ownerName={ownerName}
                addActionItem={addActionItem}
                updateActionItem={updateActionItem}
                toggleActionItem={toggleActionItem}
                onRefresh={handleRefreshActionItems}
              />
              {loadingActionItems && <Progress size="xs" isIndeterminate colorScheme="green" />}

              <IssuesSection
                issues={issues}
                rockId={rock.id}
                onIssueClick={onIssueClick}
                onResolveIssue={onResolveIssue}
                updateIssue={updateIssue}
                onStatusChange={onStatusChange}
                onRefresh={handleRefreshIssues}
                isReadOnly={isReadOnly}
              />
              {loadingIssues && <Progress size="xs" isIndeterminate colorScheme="red" />}

              {milestones.length === 0 && actionItems.length === 0 && issues.length === 0 && (
                <Text fontSize="sm" color={textColorLight} textAlign="center" py={4}>
                  No milestones, action items, or issues
                </Text>
              )}
            </VStack>
          )}
        </Collapse>

        {/* Latest Update Form - Outside Collapse */}
              {saveRockUpdate && currentUserId && (
          <Box mt={4} pt={4} borderTop="1px" borderColor="gray.200">
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontWeight="medium" fontSize="sm">
                      Latest Update
                    </Text>
                    {loadPreviousNotes && (
                      <Button
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        leftIcon={<Icon as={FileText} w={3} h={3} />}
                        onClick={onNotesModalOpen}
                      >
                        View Previous Notes ({previousNotes.length})
                      </Button>
                    )}
                  </Flex>
                  <Formik
                    initialValues={{ note: rock.notes || '' }}
                    validationSchema={updateValidationSchema}
                    onSubmit={handleSaveUpdate}
                    enableReinitialize={true} 
                  >
                    {({ isSubmitting }) => (
                      <Form>
                        <Field name="note">
                          {({ field, form }: any) => (
                            <FormControl isInvalid={form.errors.note && form.touched.note}>
                              <Textarea
                                {...field}
                                placeholder="Enter your latest update..."
                                size="sm"
                                rows={3}
                                mb={2}
                              />
                              <FormErrorMessage>{form.errors.note}</FormErrorMessage>
                            </FormControl>
                          )}
                        </Field>
                        <Button
                          type="submit"
                          size="sm"
                          colorScheme="blue"
                          isLoading={isSubmitting}
                          loadingText="Saving..."
                        >
                          Save Update
                        </Button>
                      </Form>
                    )}
                  </Formik>
                </Box>
              )}

      {/* Previous Notes Modal */}
      {loadPreviousNotes && (
        <Modal isOpen={isNotesModalOpen} onClose={onNotesModalClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Previous Notes</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {loadingNotes ? (
                <Box textAlign="center" py={8}>
                  <Spinner size="md" />
                </Box>
              ) : previousNotes.length > 0 ? (
                <VStack align="stretch" spacing={3} maxH="60vh" overflowY="auto">
                  {previousNotes.map((note) => {
                    const creator = users.find((u) => u.id === note.createdById);
                    return (
                      <Box
                        key={note.id}
                        p={4}
                        bg={notesBg}
                        borderRadius="md"
                        borderLeft="3px"
                        borderLeftColor="blue.500"
                      >
                        <Text fontSize="sm" color={textColor} mb={2} whiteSpace="pre-wrap">
                          {note.note}
                        </Text>
                        <HStack spacing={2} fontSize="xs" color={textColorLight}>
                          <Text fontWeight="medium">
                            {creator?.name || 'Unknown user'}
                          </Text>
                          <Text>•</Text>
                          <Text>{formatTimestamp(note.createdAt)}</Text>
                        </HStack>
                      </Box>
                    );
                  })}
                </VStack>
              ) : (
                <Box textAlign="center" py={8}>
                  <Text fontSize="sm" color={textColorLight}>
                    No previous notes
                  </Text>
                </Box>
              )}
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" onClick={onNotesModalClose}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Resolve Issue Modal */}
      {selectedIssueToResolve && (
        <ResolveIssueModal
          isOpen={isResolveModalOpen}
          onClose={() => {
            onResolveModalClose();
            setSelectedIssueToResolve(null);
          }}
          onConfirm={handleConfirmResolve}
          issueTitle={selectedIssueToResolve.name}
          isLastIssue={isLastIssue}
          onMarkOnTrack={handleMarkOnTrack}
        />
      )}
      </Box>
    </Card>
  );
}

// Memoize component to prevent unnecessary rerenders when props haven't changed
export const RockCardDetailed = memo(RockCardDetailedComponent, (prevProps, nextProps) => {
  // Custom comparison function to prevent rerenders
  return (
    prevProps.rock.id === nextProps.rock.id &&
    prevProps.rock.progress === nextProps.rock.progress &&
    prevProps.rock.status === nextProps.rock.status &&
    prevProps.rock.notes === nextProps.rock.notes &&
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.refreshTrigger === nextProps.refreshTrigger &&
    prevProps.refreshMilestonesTrigger === nextProps.refreshMilestonesTrigger &&
    prevProps.refreshActionItemsTrigger === nextProps.refreshActionItemsTrigger &&
    prevProps.users.length === nextProps.users.length &&
    prevProps.currentUserId === nextProps.currentUserId &&
    prevProps.aiScore === nextProps.aiScore
  );
});
